/**
 * The Properties CSV import columns (L-10; plans/L-10.md; ADR-74).
 *
 * The template's columns are the property create form's own fields under the
 * form's own labels, and every rule a cell must satisfy is the create schema's
 * rule (`propertyCreateSchema`): the import turns text into the schema's types —
 * numbers, and taxonomy names into ids — and adds no second validator (docs/18
 * §4, invariant 2).
 *
 * WHAT IS DELIBERATELY NOT A COLUMN, each for a reason that is not "later":
 * Status (a lifecycle transition, refused by 7.2-E); the sensitive access and
 * security fields (a sensitive staged run, §7c); the two party fields (they
 * name vendors, which are not imported at launch, L-26); aliases, hours and
 * photos (per-record, not in the Yardi export).
 */

import type { ImportColumn } from './import-runs.ts';

export type PropertyImportField =
  | 'name'
  | 'yardiId'
  | 'tkcNumber'
  | 'entityId'
  | 'streetNumber'
  | 'streetName'
  | 'streetSuffix'
  | 'addressLine2'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'sqFt'
  | 'yearBuilt'
  | 'propertyType'
  | 'managementType'
  | 'group'
  | 'timeZone'
  | 'tkcWorkUrl';

/** The eighteen template columns, in template order (confirmed by Matt, 2026-10-12). */
export const PROPERTY_IMPORT_COLUMNS: readonly ImportColumn<PropertyImportField>[] = [
  { header: 'Name', field: 'name', required: true, rule: '1 to 200 characters', example: '' },
  {
    header: 'Yardi ID',
    field: 'yardiId',
    required: false,
    rule: 'up to 60 characters, unique across all properties',
    example: 'ard01',
  },
  {
    header: 'TKC Number',
    field: 'tkcNumber',
    required: false,
    rule: 'up to 60 characters',
    example: '17',
  },
  {
    header: 'Entity ID',
    field: 'entityId',
    required: false,
    rule: 'up to 120 characters',
    example: 'Ardmore Plaza LLC',
  },
  {
    header: 'Street Number',
    field: 'streetNumber',
    required: false,
    rule: 'up to 30 characters',
    example: '1200',
  },
  {
    header: 'Street Name',
    field: 'streetName',
    required: false,
    rule: 'up to 200 characters',
    example: 'Ardmore',
  },
  {
    header: 'Street Suffix',
    field: 'streetSuffix',
    required: false,
    rule: 'up to 40 characters',
    example: 'Blvd',
  },
  {
    header: 'Address Line 2',
    field: 'addressLine2',
    required: false,
    rule: 'up to 200 characters',
    example: 'Suite 100',
  },
  {
    header: 'City',
    field: 'city',
    required: false,
    rule: 'up to 120 characters',
    example: 'Charlotte',
  },
  { header: 'State', field: 'state', required: false, rule: 'up to 40 characters', example: 'NC' },
  {
    header: 'ZIP',
    field: 'postalCode',
    required: false,
    rule: 'up to 20 characters',
    example: '28202',
  },
  {
    header: 'Square Feet',
    field: 'sqFt',
    required: false,
    rule: 'a whole number from 1 to 100,000,000',
    example: '125000',
  },
  {
    header: 'Year Built',
    field: 'yearBuilt',
    required: false,
    rule: 'a year from 1800 to 2100',
    example: '1998',
  },
  {
    header: 'Property Type',
    field: 'propertyType',
    required: false,
    rule: 'the name of an active property type',
    example: 'Office',
  },
  {
    header: 'Management Type',
    field: 'managementType',
    required: false,
    rule: 'the name of an active management type',
    example: 'Third party',
  },
  {
    header: 'Group',
    field: 'group',
    required: false,
    rule: 'the name of an active property group',
    example: '',
  },
  {
    header: 'Time Zone',
    field: 'timeZone',
    required: false,
    rule: 'a time zone such as America/New_York',
    example: 'America/New_York',
  },
  {
    header: 'TKC.work URL',
    field: 'tkcWorkUrl',
    required: false,
    rule: 'a full web address, including https://',
    example: '',
  },
];
